import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button, Form } from "@altimateai/lego";
import { Card, CardContent } from "@altimateai/lego";
import { LoadingButton, Stack } from '@altimateai/lego'
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { getIconByDatastoreType } from "../../DatastoreImage";
import { ConnectionProps } from "./types";
import {
  SnowflakeConnectionType,
  SnowflakePrivateKeyType,
  SnowflakeAuthKeyPair,
  DatastoreTypeEnum,
  SnowflakeDatastoreRequest,
} from "@/pages/datastores/types";
import { ConnectionHeader } from "./SnowflakeConnection/ConnectionHeader";
import { KeyPairAuthFields } from "./SnowflakeConnection/KeyPairAuthFields";
import { AccountFormField } from "./SnowflakeConnection/AccountFormField";
import { WarehouseFormField } from "./SnowflakeConnection/WarehouseFormField";
import { SnowflakeFormFields } from "./SnowflakeConnection/SnowflakeFormFields";
import { useSnowflakeConnectionTest } from "./SnowflakeConnection/useSnowflakeConnectionTest";
import { Link } from "react-router-dom";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";

const BASE64_TEST_REGEX = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;

// Base Snowflake form fields shared between both auth types
const baseFormSchema = z.object({
  account: z.string().min(1, "Account is required"),
  username: z.string().min(1, "Username is required"),
  role: z.string().min(1, "Role is required"),
  warehouse: z.string().min(1, "Warehouse is required"),
});

// Form schema with discriminated union for auth_type
const formSchema = z.discriminatedUnion("auth_type", [
  // Password authentication
  baseFormSchema.extend({
    auth_type: z.literal(SnowflakeConnectionType.PASSWORD),
    password: z.string().min(1, "Password is required"),
    key_pair: z
      .object({
        type: z.nativeEnum(SnowflakePrivateKeyType).default(SnowflakePrivateKeyType.PEM),
        private_key: z.string().optional(),
        passphrase: z.string().optional(),
      })
      .optional(),
  }),
  // Key pair authentication
  baseFormSchema.extend({
    auth_type: z.literal(SnowflakeConnectionType.KEY_PAIR),
    password: z.string().optional(),
    key_pair: z.object({
      type: z.nativeEnum(SnowflakePrivateKeyType).default(SnowflakePrivateKeyType.PEM),
      private_key: z.string().min(1, "Private key file is required"),
      passphrase: z.string().optional(),
    }),
  }),
]);

export type SnowflakeFormValues = z.infer<typeof formSchema>;

const SnowflakeConnection = ({
  typeData,
  handleConnectionSubmit,
  handleBack,
}: ConnectionProps): JSX.Element => {
  const form = useForm<SnowflakeFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      account: "",
      auth_type: SnowflakeConnectionType.KEY_PAIR,
      key_pair: {
        type: SnowflakePrivateKeyType.PEM,
        private_key: "",
        passphrase: "",
      },
      username: "",
      password: "",
      role: "",
      warehouse: "",
    },
    mode: "onBlur",
  });

  useEffect(() => {
    if (typeData?.connection) {
      const connection = typeData.connection;
      form.setValue("account", (connection.account as string) || "", { shouldValidate: true });
      form.setValue("username", (connection.username as string) || "", { shouldValidate: true });
      form.setValue(
        "auth_type",
        (connection.auth_type as SnowflakeConnectionType) || SnowflakeConnectionType.KEY_PAIR,
        { shouldValidate: true }
      );
      form.setValue("password", (connection.password as string) || "", { shouldValidate: true });
      form.setValue("role", (connection.role as string) || "", { shouldValidate: true });
      form.setValue("warehouse", (connection.warehouse as string) || "", { shouldValidate: true });

      if (connection.key_pair && typeof connection.key_pair === "object") {
        const keyPair = connection.key_pair as SnowflakeAuthKeyPair;
        form.setValue("key_pair.type", keyPair.type || SnowflakePrivateKeyType.PEM, {
          shouldValidate: true,
        });
        form.setValue("key_pair.private_key", keyPair.private_key || "", {
          shouldValidate: true,
        });
        if (BASE64_TEST_REGEX.test(keyPair.passphrase)) {
          form.setValue("key_pair.passphrase", atob(keyPair.passphrase || ""), {
            shouldValidate: true,
          });
        } else {
          form.setValue("key_pair.passphrase", keyPair.passphrase || "", {
            shouldValidate: true,
          });
        }
      }
    }
  }, [typeData.connection, form]);

  const snowflakeConnectionAuthType = form.watch("auth_type");
  const {
    connectionTestStatus,
    isTestingConnection,
    handleTestConnection,
    testConnectionAndProceed,
  } = useSnowflakeConnectionTest(form);

  const prepareConnectionData = (values: SnowflakeFormValues): SnowflakeDatastoreRequest => {
    const baseData = {
      ...typeData,
      account: values.account,
      user: values.username,
      role: values.role,
      database: "",
      warehouse: values.warehouse,
      // default to undefined to avoid connection issues when selecting keypair auth
      databases: undefined,
    };

    if (values.auth_type === SnowflakeConnectionType.PASSWORD) {
      return {
        ...baseData,
        auth_type: SnowflakeConnectionType.PASSWORD,
        password: values.password,
      };
    } else {
      return {
        ...baseData,
        auth_type: SnowflakeConnectionType.KEY_PAIR,
        key_pair: {
          private_key: values.key_pair.private_key,
          passphrase: values.key_pair.passphrase ? btoa(values.key_pair.passphrase) : "",
          type: values.key_pair.type,
        },
      };
    }
  };

  const handleTestConnectionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const values = form.getValues();
    const data = prepareConnectionData(values);
    // set empty password to avoid connection issues when selecting keypair auth
    handleTestConnection(e, { password: "", ...typeData, ...data });
  };

  const onSubmit = (values: SnowflakeFormValues) => {
    // Process values based on auth type
    const processedValues: SnowflakeFormValues =
      values.auth_type === SnowflakeConnectionType.PASSWORD
        ? values
        : {
            ...values,
            key_pair: {
              ...values.key_pair,
              passphrase: values.key_pair.passphrase
                ? btoa(values.key_pair.passphrase)
                : values.key_pair.passphrase,
            },
          };

    // If connection hasn't been tested or form is dirty since last successful test, test connection first
    if (connectionTestStatus !== "success" || form.formState.isDirty) {
      const data = prepareConnectionData(values);
      testConnectionAndProceed({ ...typeData, ...data }, () => {
        handleConnectionSubmit?.(processedValues);
      });
      return;
    }

    // Connection already tested successfully, proceed directly
    handleConnectionSubmit?.(processedValues);
  };

  const type = {
    name: "Snowflake",
    icon: getIconByDatastoreType({
      datastoreType: DatastoreTypeEnum.SNOWFLAKE,
      className: "al-w-10 al-h-10 al-mr-3",
    }),
  };

  return (
    <Card>
      <CardContent>
        <ConnectionHeader datastoreName={type.name} icon={type.icon} />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="al-mt-6">
            <Stack gap="lg" direction="column">
              <AccountFormField control={form.control} />
              <SnowflakeFormFields control={form.control} authType={snowflakeConnectionAuthType} />

              {snowflakeConnectionAuthType === SnowflakeConnectionType.KEY_PAIR && (
                <KeyPairAuthFields control={form.control} form={form} />
              )}

              <WarehouseFormField control={form.control} />

              <Stack className="al-mt-2" justify="between">
                <LoadingButton
                  variant="default"
                  disabled={
                    !form.formState.isValid ||
                    (connectionTestStatus === "failed" && !form.formState.isDirty)
                  }
                  onClick={handleTestConnectionClick}
                  loading={isTestingConnection}
                >
                  Test Connection
                </LoadingButton>
                <Stack gap="xl">
                  <Button variant="outline" asChild>
                    <Link to="/settings/datastores">Cancel</Link>
                  </Button>
                  <Stack gap="md" className="al-border-l al-ps-4">
                    <Button onClick={handleBack} variant="secondary">
                      <IconChevronLeft size={16} />
                      Back
                    </Button>
                    <Button type="submit" disabled={!form.formState.isValid}>
                      Next
                      <IconChevronRight size={16} />
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default SnowflakeConnection;
